<template>
  <div
    id="header"
    class="header-area style-two bg-white w-100"
    :class="{ 'is-pos-header': isPosPage }"
  >
    <div class="container-fluid">
      <div class="row align-items-center">
        <div
          :class="
            isPosPage
              ? 'col-xl-4 col-lg-5 col-md-5 col-sm-4'
              : 'col-xl-6 col-lg-5 col-md-5 col-sm-4'
          "
        >
          <div class="header-left d-flex align-items-center">
            <router-link to="/" class="logo">
              <img
                v-if="stateStore.logo"
                class="logo-light logo-custom"
                :src="getLogoUrl(stateStore.logo)"
                :alt="$t('header.logo_alt')"
              />
              <img
                v-else
                class="logo-light logo-custom"
                src="../../assets/img/logo.jpg"
                :alt="$t('header.default_logo_alt')"
              />
            </router-link>
            <h2 class="site-title">{{ stateStore.siteName }}</h2>
            <button
              class="header-burger-menu transition position-relative lh-1 bg-transparent p-0 border-0"
              id="header-burger-menu"
              :title="$t('header.toggle_sidebar')"
              :aria-label="$t('header.toggle_sidebar')"
              @click="toggleBurgerMenu"
            >
              <i class="menu ri-menu-line" v-show="showOpenBtn"></i>
              <i class="close ri-close-line" v-show="showCloseBtn"></i>
            </button>
          </div>
        </div>

        <div
          :class="
            isPosPage
              ? 'col-xl-8 col-lg-7 col-md-7 col-sm-8'
              : 'col-xl-6 col-lg-7 col-md-7 col-sm-8'
          "
        >
          <div class="header-right">
            <div class="header-core-actions">
              <div v-if="isPosPage" class="option-item dropdown">
                <button
                  type="button"
                  class="header-utility-button header-screen-toggle dropdown-toggle"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <i class="ri-layout-grid-line"></i>
                  <span>{{ $t("pos.screens") }}</span>
                </button>
                <div
                  class="dropdown-menu dropdown-menu-end header-screen-menu border-0 shadow-sm"
                >
                  <button
                    type="button"
                    class="dropdown-item header-screen-menu-item"
                    @click="openCustomerDisplay"
                  >
                    <i class="ri-cast-line"></i>
                    <span class="ps-2">{{ $t("pos.customer_screen") }}</span>
                  </button>
                  <button
                    type="button"
                    class="dropdown-item header-screen-menu-item"
                    @click="openPickupDisplay"
                  >
                    <i class="ri-notification-3-line"></i>
                    <span class="ps-2">{{ $t("pos.pickup_screen") }}</span>
                  </button>
                  <button
                    type="button"
                    class="dropdown-item header-screen-menu-item"
                    @click="openQueuePage"
                  >
                    <i class="ri-ticket-2-line"></i>
                    <span class="ps-2">{{ $t("pos.waiting_queue") }}</span>
                  </button>
                </div>
              </div>
              <div v-if="isPosPage" class="option-item">
                <button
                  v-if="stateStore.registerStatus.isOpen"
                  type="button"
                  class="header-utility-button header-register-button is-danger"
                  data-bs-toggle="modal"
                  data-bs-target="#closeRegisterModal"
                >
                  <i class="ri-shut-down-line"></i>
                  <span>{{ $t("pos.close_register") }}</span>
                </button>
                <button
                  v-else
                  type="button"
                  class="header-utility-button header-register-button is-success"
                  data-bs-toggle="modal"
                  data-bs-target="#openRegisterModal"
                >
                  <i class="ri-door-open-line"></i>
                  <span>{{ $t("pos.open_register") }}</span>
                </button>
              </div>
              <div v-if="!isPosPage" class="option-item">
                <router-link
                  to="/pos"
                  class="pos-link rounded-1 d-inline-block lh-1 fw-semibold transition"
                >
                  {{ $t("pos_screen") }}
                </router-link>
              </div>
              <div class="option-item">
                <ToggleFullscreenBtn />
              </div>
              <div class="option-item">
                <LanguageDropdown />
              </div>
              <div class="option-item">
                <ProfileDropdown />
              </div>
              <div class="option-item">
                <SettingsBarBtn />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useRoute, useRouter } from "vue-router";
import ToggleFullscreenBtn from "./HeaderContent/ToggleFullscreenBtn.vue";
import LanguageDropdown from "./HeaderContent/LanguageDropdown.vue";
import ProfileDropdown from "./HeaderContent/ProfileDropdown.vue";
import SettingsBarBtn from "./HeaderContent/SettingsBarBtn.vue";

import stateStore from "@/utils/store";
import { buildAssetUrl } from "@/utils/settings";

const SIDEBAR_STATE_EVENT = "app-sidebar-state-change";

export default defineComponent({
  name: "MainHeader",
  components: {
    ToggleFullscreenBtn,
    LanguageDropdown,
    ProfileDropdown,
    SettingsBarBtn,
  },

  setup() {
    const route = useRoute();
    const router = useRouter();

    const isPosPage = computed(() => route.name === "PosPage");
    const posWarehouseQuery = computed(() =>
      stateStore.selectedWarehouseId
        ? { warehouse_id: String(stateStore.selectedWarehouseId) }
        : {}
    );

    const openNamedWindow = (name: string, targetName: string) => {
      const targetRoute = router.resolve({
        name,
        query: posWarehouseQuery.value,
      });
      const openedWindow = window.open(targetRoute.href, targetName);
      openedWindow?.focus();
    };

    const openCustomerDisplay = () => {
      openNamedWindow("PosCustomerDisplayPage", "pos-customer-display");
    };

    const openPickupDisplay = () => {
      openNamedWindow("PosPickupDisplayPage", "pos-pickup-display");
    };

    const openQueuePage = () => {
      openNamedWindow("PosQueuePage", "pos-waiting-queue");
    };

    return {
      isPosPage,
      openCustomerDisplay,
      openPickupDisplay,
      openQueuePage,
      stateStore,
    };
  },

  mounted() {
    this.syncSidebarButtonState();
    window.addEventListener(SIDEBAR_STATE_EVENT, this.handleSidebarStateChange);
  },

  unmounted() {
    window.removeEventListener(
      SIDEBAR_STATE_EVENT,
      this.handleSidebarStateChange
    );
  },

  data() {
    return {
      isBurgerMenu: false,
      showOpenBtn: true,
      showCloseBtn: false,
    };
  },
  methods: {
    getLogoUrl(logo: string) {
      return buildAssetUrl(logo);
    },
    syncSidebarButtonState() {
      const isMinimized = document.body.classList.contains("minimized");
      this.isBurgerMenu = isMinimized;
      this.showOpenBtn = !isMinimized;
      this.showCloseBtn = isMinimized;
    },
    setSidebarMinimized(minimized: boolean, persist = true) {
      document.body.classList.toggle("minimized", minimized);

      if (persist) {
        localStorage.setItem("minimized", minimized.toString());
      }

      this.syncSidebarButtonState();
    },
    handleSidebarStateChange() {
      this.syncSidebarButtonState();
    },
    toggleBurgerMenu() {
      const nextState = !document.body.classList.contains("minimized");
      this.setSidebarMinimized(nextState);
      window.dispatchEvent(
        new CustomEvent(SIDEBAR_STATE_EVENT, {
          detail: { minimized: nextState },
        })
      );
    },
  },
});
</script>

<style lang="scss">
.header-area {
  box-shadow: 0px 4px 30px 0px rgba(0, 0, 0, 0.03);

  &.is-pos-header {
    &.style-two {
      padding-top: 16px;
      padding-bottom: 16px;
    }

    .header-left {
      .site-title {
        max-width: 240px;
        font-size: 22px;
      }
    }

    .header-right {
      gap: 10px;

      .header-core-actions {
        flex-wrap: nowrap;
        gap: 8px;
      }

      .option-item {
        flex-shrink: 0;
      }
    }

    .header-utility-button {
      min-height: 40px;
      padding: 8px 13px;
      border-radius: 12px;
    }

    .profile-dropdown {
      .profile-img {
        width: 36px !important;
        height: 36px !important;
        margin-right: 8px;
      }

      .md-none {
        display: block;
        min-width: 0;

        > span:first-child {
          margin-bottom: 0;
          font-size: 16px;
          line-height: 1.1;
        }

        > span:last-child {
          display: none;
        }
      }
    }
  }

  &.style-two {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 99;
    padding: {
      top: 28.5px;
      bottom: 28.5px;
    }
    .header-left {
      .header-burger-menu {
        margin-left: 10px;
        i {
          font-size: 25px;

          // &.close {
          //   display: none;
          // }
        }
      }
    }
  }
  .header-left {
    min-width: 0;
    gap: 10px;

    .logo {
      min-width: auto;
    }

    .site-title {
      margin: 0;
      min-width: 0;
      max-width: min(100%, 320px);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .search-area {
      margin-left: 30px;
      input {
        height: 55px;
        padding: 13px 35px 13px 23px;
        &::placeholder {
          opacity: 1;
          color: #8d929a;
        }
      }
    }
  }
  .header-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 14px;

    .header-core-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      flex-wrap: wrap;
      gap: 10px 14px;
    }

    .navbar-nav {
      margin-right: 25px;
      & > .nav-item {
        display: inline-block;
        margin-right: 20px;
        position: relative;
        &:last-child {
          margin-right: 0;
        }
        .nav-link {
          color: #0e1622;
          font-weight: 500;
          position: relative;
          &.dropdown-toggle {
            padding-right: 12px;
          }
          &.active,
          &:hover {
            color: var(--secondaryColor);
          }
          &.dropdown-toggle {
            &:before {
              position: absolute;
              content: "";
              right: -1px;
              top: 20px;
              background-image: url(../../assets/img/icons/down-arrow-black.svg);
              background-repeat: no-repeat;
              background-size: 10px;
              width: 10px;
              height: 10px;
            }
            &:after {
              display: none;
            }
            &.active,
            &:hover {
              &:before {
                background-image: url(../../assets/img/icons/down-arrow-2.svg);
              }
            }
          }
        }
        .dropdown-menu {
          top: 65px;
          opacity: 0;
          z-index: 99;
          width: 260px;
          display: block;
          padding: 12px 25px;
          visibility: hidden;
          transition: all 0.2s ease-in-out;
          border: none;
          transform: translate3d(0, 18px, 0);
          box-shadow: rgba(149, 157, 165, 0.2) 0px 8px 24px;
          .nav-item {
            .nav-link {
              transition: var(--transition);
              &:hover {
                padding-left: 8px;
              }
            }
          }
        }
        &:hover {
          .dropdown-menu {
            top: 50px;
            opacity: 1;
            visibility: visible;
            transform: translate3d(0, 0, 0);
          }
        }
      }
    }
    .option-item {
      margin-right: 0;

      .btn,
      .pos-link {
        white-space: nowrap;
      }

      .pos-link {
        color: #4f46e5;
        border: 2px solid var(--Purple-Gradient, #4f46e5);
        box-shadow: 0px 5px 10px 0px rgba(99, 102, 241, 0.2);
        padding: 11.5px 22.5px;
        &:hover {
          border-color: transparent;
          background-color: #4f46e5;
          color: var(--whiteColor);
        }
      }
      .notification-dropdown,
      .language-dropdown {
        .dropdown-toggle {
          &:after {
            display: none;
          }
        }
      }
      .language-dropdown {
        .dropdown-toggle {
          img {
            position: relative;
            top: 1px;
          }
        }
      }
    }
  }
}

.header-utility-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 15px;
  border-radius: 14px;
  border: 1px solid #d8e1ec;
  background: #ffffff;
  color: #0f172a;
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  transition: var(--transition);

  i {
    font-size: 16px;
    flex-shrink: 0;
  }

  &:hover {
    border-color: #c1cfde;
    background: #f8fafc;
  }
}

.header-screen-toggle {
  min-width: 122px;

  &.dropdown-toggle::after {
    margin-left: 2px;
  }
}

.header-register-button {
  &.is-success {
    background: #ecfdf3;
    border-color: #86efac;
    color: #15803d;
  }

  &.is-danger {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #dc2626;
  }
}

.header-screen-menu {
  min-width: 220px;
  padding: 8px;
  border-radius: 16px;
  margin-top: 12px !important;
  box-shadow: 0 18px 36px rgba(15, 23, 42, 0.12) !important;
}

.header-screen-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 12px;
  padding: 12px 14px;
  color: #0f172a;
  font-size: 14px;
  font-weight: 600;

  i {
    font-size: 16px;
    color: #475569;
  }

  &:hover,
  &:focus {
    background: #f8fafc;
    color: #0f172a;
  }
}

.shopcart {
  padding: 3px 7px 0 0;
  span {
    width: 17px;
    height: 17px;
    font-size: 11px;
    background: var(
      --Purple-Gradient,
      linear-gradient(132deg, #4f46e5 4.27%, #6366f1 100%)
    );
    line-height: 17px;
  }
}
.searchModal {
  overflow-x: hidden;
  .modal-dialog {
    margin: 0;
    width: 100%;
    max-width: 100%;
    padding: 30px 15px;
    background-color: var(--whiteColor);
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    .modal-content {
      border: 0;
      padding: 0;
      display: flex;
      flex-wrap: wrap;
      border-radius: 0;
      max-width: 720px;
      background-color: transparent;
      margin-left: auto;
      margin-right: auto;
      form {
        width: calc(100% - 25px);
        position: relative;
        .form-control {
          height: 55px;
          border-radius: 0;
          box-shadow: unset;
          border-radius: 5px;
          color: var(--blackColor);
          font-size: 14px;
          border: 1px solid #eeeeee;
          background-color: var(--whiteColor);
          padding-left: 18px;
          padding-right: 18px;
          &::placeholder {
            transition: var(--transition);
            color: var(--paraColor);
          }
          &:focus {
            &::-moz-placeholder {
              color: transparent;
            }

            &:-ms-input-placeholder {
              color: transparent;
            }

            &::placeholder {
              color: transparent;
            }
          }
          &:-moz-placeholder {
            -moz-transition: var(--transition);
            transition: var(--transition);
            color: var(--paragraphColor);
          }
          &:-ms-input-placeholder {
            -ms-transition: var(--transition);
            transition: var(--transition);
            color: var(--paragraphColor);
          }
        }
        button {
          top: 5px;
          border: 0;
          padding: 0;
          right: 5px;
          width: 45px;
          height: 45px;
          font-size: 17px;
          border-radius: 5px;
          position: absolute;
          color: var(--whiteColor);
          transition: var(--transition);
          background-color: var(--secondaryColor);
          &:hover {
            background-color: var(--titleColor);
            color: var(--whiteColor);
          }
          img {
            position: relative;
            top: -1px;
            filter: brightness(0) invert(1);
          }
        }
      }
      .btn-close {
        top: 13px;
        padding: 0;
        z-index: 2;
        opacity: 1;
        right: 0px;
        text-align: right;
        cursor: pointer;
        box-shadow: unset;
        position: absolute;
        background-color: transparent;
        background-image: none;
        img {
          min-width: 22px;
        }
      }
    }
  }
}
@media only screen and (max-width: 1199px) {
  .header-area {
    &.is-pos-header {
      .header-right {
        .header-core-actions {
          flex-wrap: wrap;
        }
      }

      .profile-dropdown {
        .md-none {
          display: none;
        }
      }
    }
  }

  .header-utility-button {
    min-height: 42px;
    padding: 9px 14px;
    font-size: 13px;
  }
}
@media only screen and (max-width: 991px) {
  .header-area {
    .header-left {
      .logo {
        max-width: 85px;
        min-width: 85px;
      }

      .site-title {
        max-width: 180px;
        font-size: 24px;
      }
    }
    .header-right {
      justify-content: flex-start;
      width: 100%;

      .option-item {
        .settings-toggler {
          width: 35px;
          height: 35px;
        }
        .profile-dropdown {
          .dropdown-toggle {
            img {
              width: 35px;
              height: 35px;
            }
          }
        }
      }
    }
  }
  .profile-dropdown img {
    margin-right: 0;
  }
  .navbar-toggler {
    width: 35px;
    height: 35px;
    border: 1px solid rgba(0, 0, 0, 0.1);
  }
}
@media only screen and (max-width: 767px) {
  .header-area {
    &.style-two {
      padding: 18px 0;
    }

    .header-left {
      width: 100%;
      justify-content: space-between;
      margin-bottom: 12px;

      .logo {
        max-width: 82px;
        min-width: 82px;
      }

      .site-title {
        max-width: calc(100vw - 180px);
        font-size: 20px;
      }

      .header-burger-menu {
        margin-left: 15px;
      }
    }

    .header-right {
      width: 100%;
      justify-content: flex-start;

      .header-core-actions {
        width: 100%;
        justify-content: flex-start;
        gap: 10px;
      }

      .option-item {
        .pos-link {
          font-size: 14px;
          padding: 8px 15px;
        }
      }
    }
  }

  .header-utility-button {
    min-height: 40px;
    padding: 9px 12px;
    border-radius: 12px;
  }

  .header-screen-toggle {
    min-width: auto;
  }
}
@media only screen and (max-width: 575px) {
  .header-area {
    .header-right {
      .header-core-actions {
        gap: 8px;
      }
    }
  }

  .header-utility-button {
    span {
      font-size: 12px;
    }
  }
}
@media only screen and (min-width: 1400px) {
  .header-area {
    .container-fluid {
      padding: {
        right: 50px;
        left: 50px;
      }
    }
    .header-left {
      .search-area {
        margin-left: 80px;
      }
    }
    .header-right {
      .navbar-nav {
        margin-right: 38px;
        & > .nav-item {
          margin-right: 43px;
        }
      }
    }
    &.style-two {
      .header-left {
        .header-burger-menu {
          margin-left: 20px;
        }
      }
      .header-right {
        .option-item {
          margin-right: 27px;
          &:last-child {
            margin-right: 0;
          }
        }
      }
    }
  }
}
</style>
